/* eslint-disable max-lines */
"use client";
import React, { useEffect, useState } from "react";

import Section from "@/components/common/Section/Section";
import { Minus } from "@/components/icons/Minus";
import { Plus } from "@/components/icons/Plus";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementMen } from "@/components/ui-kit/TextElementMen/TextElementMen";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { FaqAccordionItem, FaqEntryTransformed } from "@/types/common";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";
import { ThemeTypes } from "@/utils/theming";

import styles from "./FaqAccordion.module.scss";
import { buildFAQPage } from "./schemaHelper";

export interface FaqAccordionProps {
  data: FaqEntryTransformed;
  theme?: ThemeTypes;
  sectionClasses?: string;
  moduleName?: string;
}

/*
 * Contentful often wraps sentence fragments in sibling <p> tags around links.
 * Unwrap those so links stay inline, while keeping the real list markup.
 */
export const normalizeFaqHtml = (html = "") =>
  html
    .replace(/<\/p>\s*<p[^>]*>/gi, "<br/><br/>")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/p>/gi, "");

//const MenTitle = `We’re serious<br class="hidden lg:block" /> about <br class="block md:hidden" />facts.<br class="hidden md:block" /> Ask away.`;

const FaqAccordion: React.FC<FaqAccordionProps> = ({
  data,
  theme = "master",
  sectionClasses,
  moduleName,
}) => {
  const [openItem, setOpenItem] = useState<number | null>(null);
  const isMen = theme === "men";
  const trackingModuleName = (moduleName || "faq").replace(/<[^>]*>/g, "").trim();

  const handleToggle = (i: number, event: React.MouseEvent, item: FaqAccordionItem) => {
    event.preventDefault();
    const isOpen = openItem === i;
    setOpenItem(isOpen ? null : i);
    const eventName = isOpen ? "Section Collapsed" : "Section Expanded";
    segmentEvent(eventName, {
      section: item.question || item.title,
      module_name: trackingModuleName,
    });
  };

  useEffect(() => {
    const quizLink = document.querySelectorAll(".quiz-link-external-faq");
    quizLink.forEach((link) => {
      link.addEventListener("click", (e) => {
        // Ensure e.target is an HTMLElement before accessing innerText
        const target = e.target as HTMLElement;
        GA4.onMarketingCtaClicked({
          destination: "/hair/brand/quiz/",
          text: target?.innerText,
          type: "link",
          module_location: "mid_fold",
        });
      });
    });
  }, []);

  const accordions = data.accordions;

  const renderContent = (item: FaqAccordionItem, i: number) => {
    return (
      <details open={openItem === i} key={i} className={styles.accordionItem}>
        <summary
          onClick={(event) => handleToggle(i, event, item)}
          className={cn("cursor-pointer list-none", styles.accordionItemSummary)}
          aria-label={item.question || item.title}
        >
          <div className={cn("flex items-center justify-between", styles.accordionItemSummaryFlex)}>
            {isMen ? (
              <TextElementMen
                text={item.question || item.title}
                element="h3"
                classes={{
                  default: "CTA-10-Dia-Mono",
                  768: "CTA-20-Dia-Mono",
                  1024: "CTA-20-Dia-Mono",
                }}
                className="lg:[.env-windows_&]:!text-[15px]" // Windows font height adjustment
              />
            ) : (
              <TextElementNova
                text={item.question || item.title}
                element="h3"
                className="Body-20-Dia-Strong"
              />
            )}
            <div
              className={cn(
                "min-w-6 w-6 min-h-6 h-6 rounded-sm flex items-center justify-center",
                styles.accordionControl,
                {
                  [styles.opened]: openItem === i,
                  [styles.closed]: openItem !== i,
                }
              )}
            >
              {openItem === i ? (
                <Minus className="text-nova-base-black" />
              ) : (
                <Plus className="text-nova-base-black" />
              )}
            </div>
          </div>
        </summary>
        <div
          className={cn(
            "textelement nova Body-10-Dia-Normal text-nova-base-black",
            styles.accordionContent
          )}
        >
          {/* innerHTML is for UL/LI; button must be a sibling, not children */}
          <div
            dangerouslySetInnerHTML={{
              __html: normalizeFaqHtml(item.answer || item.content || ""),
            }}
          />
          {item.useButton && item.buttonLink && item.buttonLabel ? (
            <div className="pt-4">
              <ButtonNova
                level="Secondary"
                icon="Right"
                ctaLink={item.buttonLink}
                label={item.buttonLabel}
                className="w-fit"
                trackObject={{ module_location: "mid_fold", type: "button" }}
              />
            </div>
          ) : null}
        </div>
      </details>
    );
  };

  return (
    <Section id={data?.id || null} useMargin={true} className={sectionClasses}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildFAQPage(accordions)),
        }}
      />
      <div
        className={cn(
          `flex flex-wrap flex-col gap-10 ${data?.orientation === "vertical" ? "lg:gap-10" : "lg:flex-row lg:gap-20"}`,
          styles.backgroundWrapper
        )}
      >
        <div className="w-full lg:flex-1">
          {data?.title ? (
            <div className="lg:pr-6 xl:pr-12">
              {isMen ? (
                <TextElementMen
                  text={data?.title}
                  element="h2"
                  className="Title-30-Super-Normal uppercase text-center lg:text-left"
                />
              ) : (
                <TextElementNova
                  text={data?.title}
                  element="h2"
                  className="Title-20-Super-Normal"
                />
              )}
              {data?.button && (
                <div className="mt-10">
                  <ButtonNova
                    level="Tertiary"
                    icon="Left"
                    ctaLink={data.button.url}
                    label={data.button.text}
                    className={cn("w-fit mx-auto lg:mx-0", !isMen && "justify-start")}
                    trackObject={{ module_location: "mid_fold", type: "link" }}
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              {data?.heading ? (
                <div className="mb-4">
                  {isMen ? (
                    <TextElementMen
                      text={data?.heading}
                      element="h2"
                      className="Title-30-Super-Normal"
                    />
                  ) : (
                    <TextElementNova
                      text={data?.heading}
                      element="h2"
                      className="Title-20-Super-Normal"
                    />
                  )}
                  {data?.button && (
                    <div className="mt-3">
                      <ButtonNova
                        level="Textlink"
                        icon="Left"
                        ctaLink={data.button.url}
                        label={data.button.text}
                        onClick={() => {
                          GA4.onMarketingCtaClicked({
                            destination: data.button.url,
                            module_location: "mid_fold",
                            text: data.button.text,
                            type: "button",
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="w-full lg:flex-1">
          <div className={styles.accordionWrapper}>
            <>{accordions?.map((item, i) => renderContent(item, i))}</>
          </div>
        </div>
      </div>
    </Section>
  );
};
export default FaqAccordion;
